"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// ── UI-only constants ──
const CALC_METHODS = [
  { id: "cpm",       label: "Cost Per Mile (CPM)",    desc: "FSC × Miles Driven" },
  { id: "pct",       label: "% of Linehaul",          desc: "FSC % × Base Rate" },
  { id: "flat",      label: "Flat Rate Override",      desc: "Custom $/mile or flat $ amount" },
  { id: "formula",   label: "Formula-Based (Custom)", desc: "(Current − Base) ÷ MPG × Miles" },
  { id: "cta_table", label: "CTA Table (CAD)",        desc: "Fuel price (CAD/L) + linehaul (CAD)" },
];

const FUEL_TYPES = ["Diesel", "Gasoline", "LNG", "CNG", "Propane"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://api.staging.integratedtech.ca";

const fmt2 = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";

const fmtc = (n) => `$${fmt2(n)}`;

// ── Animated number counter (pure UI) ──
function AnimNum({ value, prefix = "$", decimals = 2 }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = value;
    const dur = 600;
    const startTime = performance.now();
    const frame = (now) => {
      const p = Math.min((now - startTime) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (end - start) * ease);
      if (p < 1) requestAnimationFrame(frame);
      else { setDisplay(end); prev.current = end; }
    };
    requestAnimationFrame(frame);
  }, [value]);
  return (
    <span>
      {prefix}
      {display.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
    </span>
  );
}

export default function FuelSurchargeCalc() {
  const navigate = useNavigate();

  // ── Input state ──
  const [method,    setMethod]    = useState("cpm");
  const [fuelPrice, setFuelPrice] = useState("3.85");
  const [fuelType,  setFuelType]  = useState("Diesel");
  const [miles,     setMiles]     = useState("500");
  const [linehaul,  setLinehaul]  = useState("1200");
  const [flatCpm,   setFlatCpm]   = useState("0.18");
  const [flatTotal, setFlatTotal] = useState("");
  const [baseFuel,  setBaseFuel]  = useState("1.25");
  const [mpg,       setMpg]       = useState("6.5");

  // CTA-specific inputs (CAD)
  const [ctaFuelPrice, setCtaFuelPrice] = useState("1.65");  // CAD/L
  const [ctaLinehaul,  setCtaLinehaul]  = useState("1500");  // CAD

  // ── UI state ──
  const [showTable,     setShowTable]     = useState(false);
  const [history,       setHistory]       = useState([]);
  const [saved,         setSaved]         = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [apiError,      setApiError]      = useState("");
  const [showUpgrade,   setShowUpgrade]   = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Result state (everything from API) ──
  const [result, setResult] = useState(null);

  const token = localStorage.getItem("auth_token");

  const price = parseFloat(fuelPrice) || 0;
  const lh    = parseFloat(linehaul)  || 0;
  const mi    = parseFloat(miles)     || 0;

  // ── Gauge (pure UI helper) ──
  const gaugePct   = Math.min(100, Math.max(0, ((price - 1.25) / (5.0 - 1.25)) * 100));
  const gaugeColor = gaugePct < 33 ? "#22c55e" : gaugePct < 66 ? "#f59e0b" : "#ef4444";

  // ── Fetch history from API ──
  const fetchHistory = async () => {
    if (!token) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/history/fuel-surcharge?limit=50&skip=0`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { navigate("/login"); return; }
      if (res.ok) {
        const data = await res.json();
        setHistory(data.records || []);
      }
    } catch (e) {
      console.error("Failed to fetch history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch history on mount
  useEffect(() => { fetchHistory(); }, []); // eslint-disable-line

  // ── Load FSC rates table from API ──
  const [ratesTable, setRatesTable] = useState([]);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/fuel-surcharge/rates`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.rates) setRatesTable(data.rates); })
      .catch(() => {});
  }, []);

  // ── Debounced calculation API call ──
  useEffect(() => {
    const controller = new AbortController();

    const fetchCalc = async () => {
      // For cta_table, use its own price field
      const activePrice = method === "cta_table" ? parseFloat(ctaFuelPrice) || 0 : price;
      if (!activePrice) { setResult(null); return; }

      setLoading(true);
      setApiError("");

      // Build payload — fuelType excluded per backend spec
      const payload = { method };
      if (method === "cta_table") {
        payload.fuelPrice = parseFloat(ctaFuelPrice) || 0;
        payload.linehaul  = parseFloat(ctaLinehaul)  || 0;
        payload.currency  = "CAD";
      } else {
        payload.fuelPrice = price;
        payload.miles     = mi;
        if (method === "pct" || method === "formula") payload.linehaul = lh;
        if (method === "flat") {
          payload.flatCpm   = parseFloat(flatCpm)   || 0;
          payload.flatTotal = parseFloat(flatTotal) || 0;
        }
        if (method === "formula") {
          payload.baseFuel = parseFloat(baseFuel) || 0;
          payload.mpg      = parseFloat(mpg)      || 0;
        }
      }

      try {
        const res = await fetch(`${BACKEND_URL}/api/fuel-surcharge/calculate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (res.status === 401) { navigate("/login"); return; }

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || err.detail || "Calculation failed");
        }

        const data = await res.json();
        setResult(data);
        // Show upgrade nudge if backend says this user can't save
        if (data.saved_to_history === false) setShowUpgrade(true);
        else setShowUpgrade(false);
      } catch (e) {
        if (e.name !== "AbortError") setApiError(e.message);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchCalc, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [method, fuelPrice, miles, linehaul, flatCpm, flatTotal, baseFuel, mpg, ctaFuelPrice, ctaLinehaul]); // eslint-disable-line

  // ── Save to history ──
  const saveToHistory = async () => {
    if (!result || !token) return;
    setSaved(true);
    await fetchHistory();          // re-fetch real history after save
    setTimeout(() => setSaved(false), 2000);
  };

  // ── Safely unpack API result ──
  const surcharge      = result?.surcharge      ?? 0;
  const surchargeLabel = result?.surchargeLabel ?? "—";
  const breakdown      = result?.breakdown      ?? [];
  const totalWithLH    = result?.totalWithLH    ?? 0;
  const fscPercent     = result?.fscPercent     ?? 0;
  const fsc            = result?.fsc            ?? { cpm: 0, pct: 0, min: 0, max: 9999 };
  // Use API rates if loaded, otherwise fall back to fscTable in the result
  const fscTable       = ratesTable.length > 0 ? ratesTable : (result?.fscTable ?? []);

  const isCta = method === "cta_table";

  return (
    <div style={{ minHeight: "100vh", background: "#0c0c0f", fontFamily: "'Outfit', 'Segoe UI', sans-serif", color: "#e8e8ed" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: #2a2a35; border-radius: 4px; }
        .fi { background:#16161d; border:1.5px solid #2a2a38; color:#e8e8ed; font-family:'Outfit',sans-serif; font-size:14px; padding:11px 14px; border-radius:9px; outline:none; width:100%; transition:border-color 0.2s, box-shadow 0.2s; }
        .fi:focus { border-color:#e85d04; box-shadow:0 0 0 3px #e85d0420; }
        .fi::placeholder { color:#3d3d4e; }
        select.fi option { background:#16161d; }
        .lbl { font-size:11px; font-weight:600; color:#5a5a6e; letter-spacing:1.2px; text-transform:uppercase; display:block; margin-bottom:6px; }
        .method-btn { background:#16161d; border:1.5px solid #2a2a38; color:#6b6b80; font-family:'Outfit',sans-serif; font-size:12px; font-weight:500; padding:12px 16px; border-radius:10px; cursor:pointer; text-align:left; transition:all 0.2s; width:100%; }
        .method-btn:hover { border-color:#e85d04; color:#e8e8ed; }
        .method-btn.active { background:linear-gradient(135deg,#e85d0418,#dc2f0218); border-color:#e85d04; color:#fff; }
        .method-btn .m-label { font-size:13px; font-weight:700; color:inherit; margin-bottom:2px; }
        .method-btn .m-desc { font-size:11px; color:#5a5a6e; }
        .method-btn.active .m-desc { color:#e85d0480; }
        .glow-card { background:linear-gradient(135deg,#16161d,#111118); border:1px solid #2a2a38; border-radius:14px; padding:24px; }
        .result-big { font-family:'JetBrains Mono',monospace; font-size:52px; font-weight:600; background:linear-gradient(135deg,#e85d04,#dc2f02); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; line-height:1; letter-spacing:-2px; }
        .bd-row { display:flex; justify-content:space-between; align-items:center; padding:10px 0; border-bottom:1px solid #1e1e28; font-size:13px; }
        .bd-row:last-child { border-bottom:none; }
        .bd-row .bd-label { color:#5a5a6e; }
        .bd-row .bd-val { font-family:'JetBrains Mono',monospace; font-size:13px; color:#c8c8d8; }
        .bd-row.hl .bd-label { color:#e85d04; font-weight:600; }
        .bd-row.hl .bd-val { color:#e85d04; font-weight:700; font-size:14px; }
        .gauge-track { height:8px; background:#1e1e28; border-radius:99px; overflow:hidden; margin:8px 0; }
        .gauge-fill { height:100%; border-radius:99px; transition:width 0.6s cubic-bezier(0.34,1.56,0.64,1), background 0.4s; }
        .price-display { font-family:'JetBrains Mono',monospace; font-size:36px; font-weight:600; letter-spacing:-1px; }
        .tag-pill { display:inline-flex; align-items:center; gap:5px; background:#1e1e28; border:1px solid #2a2a38; border-radius:20px; padding:4px 12px; font-size:11px; color:#6b6b80; }
        .save-btn { background:linear-gradient(135deg,#e85d04,#dc2f02); color:#fff; border:none; font-family:'Outfit',sans-serif; font-size:13px; font-weight:700; padding:12px 28px; border-radius:9px; cursor:pointer; letter-spacing:0.3px; transition:opacity 0.2s, transform 0.1s; }
        .save-btn:hover:not(:disabled) { opacity:0.9; transform:translateY(-1px); }
        .save-btn:disabled { opacity:0.4; cursor:not-allowed; }
        .fade-in { animation:fi 0.4s ease; }
        @keyframes fi { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .slide-in { animation:si 0.3s ease; }
        @keyframes si { from{opacity:0;transform:translateX(-10px)} to{opacity:1;transform:translateX(0)} }
        .table-row-hover:hover { background:#1a1a24 !important; }
        .active-row { background:#e85d0412 !important; }
        input[type=range] { -webkit-appearance:none; width:100%; height:4px; background:transparent; outline:none; cursor:pointer; }
        input[type=range]::-webkit-slider-runnable-track { height:4px; background:linear-gradient(to right,#e85d04 0%,#e85d04 var(--val,0%),#2a2a38 var(--val,0%),#2a2a38 100%); border-radius:99px; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:18px; height:18px; background:#e85d04; border:2px solid #0c0c0f; border-radius:50%; margin-top:-7px; box-shadow:0 0 0 3px #e85d0430; transition:box-shadow 0.2s; }
        input[type=range]::-webkit-slider-thumb:hover { box-shadow:0 0 0 5px #e85d0440; }
        .dimmed { opacity:0.5; transition:opacity 0.2s; }
        .upgrade-banner { background:linear-gradient(135deg,#1a1208,#1a0e00); border:1px solid #e85d0450; border-radius:10px; padding:14px 18px; margin-bottom:16px; display:flex; justify-content:space-between; align-items:center; }
      `}</style>

      {/* HEADER */}
      <div style={{ borderBottom:"1px solid #1e1e28", padding:"18px 32px", display:"flex", alignItems:"center", justifyContent:"space-between", background:"#0c0c0f", position:"sticky", top:0, zIndex:50 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:38, height:38, background:"linear-gradient(135deg,#e85d04,#dc2f02)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>⛽</div>
          <div>
            <div style={{ fontWeight:800, fontSize:17, letterSpacing:-0.5 }}>Fuel Surcharge Calculator</div>
            <div style={{ fontSize:11, color:"#5a5a6e", letterSpacing:0.5 }}>
              DOE-based · 5 calculation methods
              {loading && <span style={{ color:"#e85d04", marginLeft:8 }}>· calculating…</span>}
            </div>
          </div>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div className="tag-pill">⛽ {fuelType}</div>
          {!isCta && <div className="tag-pill" style={{ color:gaugeColor, borderColor:gaugeColor+"40" }}>${fmt2(price)}/gal</div>}
          {isCta  && <div className="tag-pill" style={{ color:"#60a5fa", borderColor:"#60a5fa40" }}>${fmt2(parseFloat(ctaFuelPrice)||0)} CAD/L</div>}
          <button onClick={() => setShowTable(!showTable)} style={{ background:showTable?"#e85d0420":"#16161d", border:`1px solid ${showTable?"#e85d04":"#2a2a38"}`, color:showTable?"#e85d04":"#6b6b80", fontFamily:"'Outfit',sans-serif", fontSize:12, fontWeight:600, padding:"7px 16px", borderRadius:8, cursor:"pointer" }}>
            FSC Table
          </button>
        </div>
      </div>

      {/* MAIN GRID */}
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"32px 24px 60px", display:"grid", gridTemplateColumns:"1fr 380px", gap:24 }}>

        {/* LEFT */}
        <div>

          {/* Upgrade banner — shown when saved_to_history is false */}
          {showUpgrade && (
            <div className="upgrade-banner fade-in">
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:"#f59e0b", marginBottom:3 }}>History not saved — Free plan</div>
                <div style={{ fontSize:12, color:"#5a5a6e" }}>Upgrade to Pro to save calculations and access your history.</div>
              </div>
              <button
                onClick={() => navigate("/billing")}
                style={{ background:"linear-gradient(135deg,#e85d04,#dc2f02)", color:"#fff", border:"none", fontFamily:"'Outfit',sans-serif", fontSize:12, fontWeight:700, padding:"8px 18px", borderRadius:8, cursor:"pointer", whiteSpace:"nowrap" }}
              >
                Upgrade →
              </button>
            </div>
          )}

          {/* Fuel Price card — hidden for CTA method */}
          {!isCta && (
            <div className="glow-card fade-in" style={{ marginBottom:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                <div>
                  <div className="lbl">Current Diesel / Fuel Price</div>
                  <div className="price-display" style={{ color:gaugeColor }}>${fmt2(price)}</div>
                  <div style={{ fontSize:11, color:"#5a5a6e", marginTop:4 }}>per gallon · {fuelType}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div className="lbl">Fuel Type</div>
                  <select className="fi" style={{ width:140 }} value={fuelType} onChange={e => setFuelType(e.target.value)}>
                    {FUEL_TYPES.map(f => <option key={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <input type="range" min="1.00" max="6.00" step="0.01" value={price} style={{ "--val":`${((price-1)/5)*100}%` }} onChange={e => setFuelPrice(e.target.value)} />
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#3d3d4e", marginTop:4 }}>
                <span>$1.00</span><span>$2.00</span><span>$3.00</span><span>$4.00</span><span>$5.00</span><span>$6.00</span>
              </div>
              <div style={{ marginTop:16 }}>
                <label className="lbl">Or type exact price</label>
                <input className="fi" type="number" step="0.01" placeholder="3.85" value={fuelPrice} onChange={e => setFuelPrice(e.target.value)} style={{ width:180 }} />
              </div>
              <div style={{ marginTop:16, padding:"12px 16px", background:"#0f0f15", borderRadius:9, border:"1px solid #1e1e28" }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:11, color:"#5a5a6e" }}>Price Level</span>
                  <span style={{ fontSize:11, color:gaugeColor, fontWeight:600 }}>
                    {gaugePct < 33 ? "LOW — Minimal surcharge" : gaugePct < 55 ? "MODERATE" : gaugePct < 77 ? "HIGH — Significant surcharge" : "VERY HIGH"}
                  </span>
                </div>
                <div className="gauge-track"><div className="gauge-fill" style={{ width:`${gaugePct}%`, background:gaugeColor }} /></div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"#3d3d4e", marginTop:4 }}>
                  <span>$1.25 (base)</span><span>$3.00</span><span>$4.75+</span>
                </div>
              </div>
            </div>
          )}

          {/* Method Tabs */}
          <div className="glow-card fade-in" style={{ marginBottom:20 }}>
            <div className="lbl" style={{ marginBottom:14 }}>Calculation Method</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:20 }}>
              {CALC_METHODS.map(m => (
                <button key={m.id} className={`method-btn${method===m.id?" active":""}`} onClick={() => setMethod(m.id)}>
                  <div className="m-label">{m.label}</div>
                  <div className="m-desc">{m.desc}</div>
                </button>
              ))}
            </div>

            {/* Dynamic form fields per method */}
            <div className="slide-in" key={method}>
              {(method==="cpm") && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                  <div><label className="lbl">Miles Driven</label><input className="fi" type="number" placeholder="500" value={miles} onChange={e => setMiles(e.target.value)} /></div>
                  <div><label className="lbl">Fuel Price (USD/gal)</label><input className="fi" type="number" step="0.01" placeholder="3.85" value={fuelPrice} onChange={e => setFuelPrice(e.target.value)} /></div>
                </div>
              )}
              {(method==="pct") && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
                  <div><label className="lbl">Miles Driven</label><input className="fi" type="number" placeholder="500" value={miles} onChange={e => setMiles(e.target.value)} /></div>
                  <div><label className="lbl">Linehaul Rate ($)</label><input className="fi" type="number" placeholder="1200.00" value={linehaul} onChange={e => setLinehaul(e.target.value)} /></div>
                  <div><label className="lbl">Fuel Price (USD/gal)</label><input className="fi" type="number" step="0.01" placeholder="3.85" value={fuelPrice} onChange={e => setFuelPrice(e.target.value)} /></div>
                </div>
              )}
              {method==="flat" && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
                  <div><label className="lbl">Miles</label><input className="fi" type="number" placeholder="500" value={miles} onChange={e => setMiles(e.target.value)} /></div>
                  <div><label className="lbl">Custom $/Mile Rate</label><input className="fi" type="number" step="0.001" placeholder="0.180" value={flatCpm} onChange={e => { setFlatCpm(e.target.value); setFlatTotal(""); }} /></div>
                  <div><label className="lbl">— OR — Flat $ Total</label><input className="fi" type="number" placeholder="90.00" value={flatTotal} onChange={e => { setFlatTotal(e.target.value); setFlatCpm(""); }} /></div>
                </div>
              )}
              {method==="formula" && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:14 }}>
                  <div><label className="lbl">Miles</label><input className="fi" type="number" placeholder="500" value={miles} onChange={e => setMiles(e.target.value)} /></div>
                  <div><label className="lbl">Base Fuel Price ($)</label><input className="fi" type="number" step="0.01" placeholder="1.25" value={baseFuel} onChange={e => setBaseFuel(e.target.value)} /></div>
                  <div><label className="lbl">Truck MPG</label><input className="fi" type="number" step="0.1" placeholder="6.5" value={mpg} onChange={e => setMpg(e.target.value)} /></div>
                  <div><label className="lbl">Linehaul ($)</label><input className="fi" type="number" placeholder="1200" value={linehaul} onChange={e => setLinehaul(e.target.value)} /></div>
                </div>
              )}
              {method==="cta_table" && (
                <div>
                  <div style={{ marginBottom:12, padding:"8px 14px", background:"#0f1520", border:"1px solid #1e3a5f", borderRadius:8, fontSize:12, color:"#60a5fa" }}>
                    🍁 CTA (Canadian Trucking Alliance) rate table — inputs in Canadian dollars
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                    <div>
                      <label className="lbl">Fuel Price (CAD/L)</label>
                      <input className="fi" type="number" step="0.001" placeholder="1.65" value={ctaFuelPrice} onChange={e => setCtaFuelPrice(e.target.value)} />
                    </div>
                    <div>
                      <label className="lbl">Linehaul Rate (CAD)</label>
                      <input className="fi" type="number" placeholder="1500.00" value={ctaLinehaul} onChange={e => setCtaLinehaul(e.target.value)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* API Error */}
          {apiError && (
            <div style={{ background:"#2a1010", border:"1px solid #7f2020", borderRadius:9, padding:"12px 16px", fontSize:13, color:"#f87171", marginBottom:16 }}>
              ⚠ {apiError}
            </div>
          )}

          {/* FSC Table — loaded from API */}
          {showTable && (
            <div className="glow-card fade-in" style={{ marginBottom:20 }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:14, color:"#e8e8ed" }}>
                DOE Fuel Surcharge Schedule
                <span style={{ fontSize:11, color:"#5a5a6e", fontWeight:400, marginLeft:8 }}>Based on National Average Diesel Price</span>
              </div>
              <div style={{ maxHeight:280, overflowY:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead style={{ position:"sticky", top:0 }}>
                    <tr style={{ background:"#0c0c0f" }}>
                      {["Price Range","CPM Rate","% of Load"].map(h => (
                        <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:10, color:"#5a5a6e", letterSpacing:1, fontWeight:600, borderBottom:"1px solid #1e1e28" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fscTable.length === 0 ? (
                      <tr><td colSpan={3} style={{ padding:"20px 12px", textAlign:"center", color:"#3d3d4e", fontSize:12 }}>Loading rates…</td></tr>
                    ) : fscTable.map((row, i) => (
                      <tr key={i} className={`table-row-hover${row.isActive?" active-row":""}`} style={{ background:"transparent" }}>
                        <td style={{ padding:"7px 12px", fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:row.isActive?"#e85d04":"#6b6b80" }}>{row.isActive&&"▶ "}{row.range}</td>
                        <td style={{ padding:"7px 12px", fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:row.isActive?"#e85d04":"#5a5a6e" }}>{row.cpmDisplay}</td>
                        <td style={{ padding:"7px 12px", fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:row.isActive?"#e85d04":"#5a5a6e" }}>{row.pctDisplay}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* History — from API */}
          {(historyLoading || history.length > 0) && (
            <div className="glow-card fade-in">
              <div style={{ fontWeight:700, fontSize:13, marginBottom:14 }}>Recent Calculations</div>
              {historyLoading ? (
                <div style={{ textAlign:"center", padding:"20px 0", fontSize:12, color:"#5a5a6e" }}>Loading history…</div>
              ) : history.map((h, i) => (
                <div key={h.id || i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:i<history.length-1?"1px solid #1e1e28":"none", fontSize:12 }}>
                  <div>
                    <span style={{ color:"#6b6b80" }}>{new Date(h.created_at).toLocaleDateString()}</span>
                    <span style={{ color:"#3d3d4e", margin:"0 8px" }}>·</span>
                    <span style={{ color:"#c8c8d8" }}>{h.data?.method || "—"}</span>
                    <span style={{ color:"#3d3d4e", margin:"0 8px" }}>·</span>
                    <span style={{ color:"#6b6b80" }}>${fmt2(h.data?.fuel_price ?? 0)}/gal</span>
                  </div>
                  <span style={{ fontFamily:"'JetBrains Mono',monospace", color:"#e85d04", fontWeight:600 }}>
                    ${fmt2(h.data?.surcharge_amount ?? 0)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — Results */}
        <div style={{ position:"sticky", top:88, alignSelf:"start" }}>
          <div className={loading?"dimmed":""} style={{ background:"linear-gradient(145deg,#16161d,#111118)", border:"1px solid #2a2a38", borderRadius:16, padding:"28px 24px", marginBottom:16, position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-40, right:-40, width:160, height:160, background:"#e85d0415", borderRadius:"50%", filter:"blur(40px)", pointerEvents:"none" }} />
            <div style={{ fontSize:11, color:"#5a5a6e", letterSpacing:1.5, textTransform:"uppercase", marginBottom:8 }}>Fuel Surcharge</div>
            <div className="result-big"><AnimNum value={surcharge} /></div>
            <div style={{ fontSize:12, color:"#3d3d4e", marginTop:8, fontFamily:"'JetBrains Mono',monospace" }}>{surchargeLabel}</div>
            <div style={{ height:1, background:"#1e1e28", margin:"20px 0" }} />
            {breakdown.map((b, i) => (
              <div key={i} className={`bd-row${b.highlight?" hl":""}`}>
                <span className="bd-label">{b.label}</span>
                <span className="bd-val">{b.value}</span>
              </div>
            ))}
            {lh > 0 && (method==="pct"||method==="formula") && (
              <>
                <div style={{ height:1, background:"#2a2a38", margin:"14px 0" }} />
                <div className="bd-row"><span className="bd-label">Linehaul Rate</span><span className="bd-val">{fmtc(lh)}</span></div>
                <div className="bd-row">
                  <span className="bd-label" style={{ color:"#e8e8ed", fontWeight:600 }}>Total (LH + FSC)</span>
                  <span className="bd-val" style={{ color:"#e8e8ed", fontWeight:700, fontSize:15 }}><AnimNum value={totalWithLH} /></span>
                </div>
                <div className="bd-row">
                  <span className="bd-label">FSC as % of Linehaul</span>
                  <span className="bd-val" style={{ color:"#f59e0b" }}>{fmt2(fscPercent)}%</span>
                </div>
              </>
            )}
            {method==="cta_table" && totalWithLH > 0 && (
              <>
                <div style={{ height:1, background:"#2a2a38", margin:"14px 0" }} />
                <div className="bd-row">
                  <span className="bd-label" style={{ color:"#e8e8ed", fontWeight:600 }}>Total (LH + FSC)</span>
                  <span className="bd-val" style={{ color:"#60a5fa", fontWeight:700, fontSize:15 }}><AnimNum value={totalWithLH} /></span>
                </div>
              </>
            )}
          </div>

          <div style={{ background:"#16161d", border:"1px solid #2a2a38", borderRadius:12, padding:"16px 20px", marginBottom:16, display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div>
              <div className="lbl">DOE CPM Rate</div>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:22, fontWeight:600, color:"#e85d04" }}>
                ${fsc.cpm.toFixed(3)}<span style={{ fontSize:12, color:"#5a5a6e" }}>/mi</span>
              </div>
            </div>
            <div>
              <div className="lbl">DOE % Rate</div>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:22, fontWeight:600, color:"#f59e0b" }}>
                {fsc.pct.toFixed(1)}<span style={{ fontSize:12, color:"#5a5a6e" }}>%</span>
              </div>
            </div>
            <div style={{ gridColumn:"span 2" }}>
              <div className="lbl">Active Price Band</div>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:"#5a5a6e" }}>
                ${fsc.min.toFixed(3)} – {fsc.max===9999?"∞":`$${fsc.max.toFixed(3)}`} /gal
              </div>
            </div>
          </div>

          <button className="save-btn" style={{ width:"100%" }} onClick={saveToHistory} disabled={!result || showUpgrade}>
            {saved ? "✓ Saved to History" : "Save Calculation"}
          </button>
          {showUpgrade && (
            <div style={{ marginTop:8, fontSize:11, color:"#e85d04", textAlign:"center" }}>
              Upgrade to Pro to save calculations
            </div>
          )}
          <div style={{ marginTop:12, fontSize:11, color:"#3d3d4e", textAlign:"center", lineHeight:1.6 }}>
            Rates based on DOE National Average Diesel Price schedule.<br />
            Always verify with current weekly DOE pricing.
          </div>
        </div>
      </div>
    </div>
  );
}
